import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const BRANDING_KEYS = [
  "app_name",
  "app_tagline",
  "app_subtitle",
  "app_logo_url",
  "app_favicon_url",
  "app_apple_touch_icon_url",
  "app_og_image_url",
] as const;

export type BrandingKey = (typeof BRANDING_KEYS)[number];

const DEFAULTS: Record<BrandingKey, string> = {
  app_name: "WhisprAI",
  app_tagline: "Chat. Match. Connect.",
  app_subtitle: "Chat · Dating · Marketplace · Career Portal · 100+ AI Experts",
  app_logo_url: "",
  app_favicon_url: "/favicon.png",
  app_apple_touch_icon_url: "/apple-touch-icon.png",
  app_og_image_url: "/og-image-v6.png",
};

let cachedBranding: Record<BrandingKey, string> | null = null;
let fetchPromise: Promise<Record<BrandingKey, string>> | null = null;

async function fetchBranding(): Promise<Record<BrandingKey, string>> {
  const { data } = await supabase
    .from("chatbot_settings")
    .select("key, value")
    .in("key", BRANDING_KEYS as unknown as string[]);

  const result = { ...DEFAULTS };
  if (data) {
    for (const row of data) {
      if (row.value && row.key in result) {
        (result as any)[row.key] = row.value;
      }
    }
  }
  cachedBranding = result;
  return result;
}

export function useAppBranding() {
  const [branding, setBranding] = useState<Record<BrandingKey, string>>(
    cachedBranding ?? DEFAULTS
  );

  useEffect(() => {
    if (cachedBranding) {
      setBranding(cachedBranding);
      return;
    }
    if (!fetchPromise) fetchPromise = fetchBranding();
    fetchPromise.then(setBranding);
  }, []);

  // Swap favicon & apple-touch-icon dynamically
  useEffect(() => {
    if (branding.app_favicon_url && branding.app_favicon_url !== DEFAULTS.app_favicon_url) {
      const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
      if (link) link.href = branding.app_favicon_url;
    }
    if (branding.app_apple_touch_icon_url && branding.app_apple_touch_icon_url !== DEFAULTS.app_apple_touch_icon_url) {
      const link = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement | null;
      if (link) link.href = branding.app_apple_touch_icon_url;
    }
  }, [branding.app_favicon_url, branding.app_apple_touch_icon_url]);

  return branding;
}

/** Force refetch (used after admin saves) */
export function invalidateBrandingCache() {
  cachedBranding = null;
  fetchPromise = null;
}

export { DEFAULTS as BRANDING_DEFAULTS, BRANDING_KEYS };
